import { AutomobileMaintenanceSchedules } from '@/reports/types/vehicles';
import {
  TabsNavigation,
  TabData,
} from '@/theme/components/TabsNavigation/TabsNavigation';
import { Card, ColumnValue, Stack, SwitchLabel, Text } from '@/theme/index';
import { MajorTableContent, StyledTab } from './components';

interface MajorMaintenanceProps {
  vehicleMaintenanceSchedules: AutomobileMaintenanceSchedules;
  isSevereDriving: boolean;
  setIsSevereDriving: (value: boolean) => void;
  hasSevereDrivingItems?: boolean;
}

export function MajorMaintenance({
  vehicleMaintenanceSchedules,
  isSevereDriving,
  setIsSevereDriving,
  hasSevereDrivingItems = true,
}: MajorMaintenanceProps) {
  const [mileageMaintenance, monthsMaintenance] = ['miles', 'months'].map(
    (maintenanceUnit) => {
      return generateMajorMaintenanceData(
        vehicleMaintenanceSchedules,
        maintenanceUnit
      );
    }
  );

  const maintenanceTabs: TabData[] = [
    {
      label: 'By Mileage',
      content: MajorTableContent(mileageMaintenance),
    },
    {
      label: 'By Months',
      content: MajorTableContent(monthsMaintenance),
    },
  ];

  return (
    <Card variant="outlined">
      <Stack
        direction={{ md: 'row', xs: 'column' }}
        width="100%"
        justifyContent="space-between"
        mb={2}
      >
        <Text variant="h2">Recommended Major Services</Text>
        {hasSevereDrivingItems && (
          <SwitchLabel
            label="Severe Driving"
            tooltip="Demanding or harsh driving conditions that can accelerate wear and tear on your vehicle."
            checked={isSevereDriving}
            setChecked={setIsSevereDriving}
            tooltipStart
          />
        )}
      </Stack>
      <Text variant="body2">
        As your vehicle reaches significant mileage milestones, it's crucial to
        provide it with specialized care to keep it running smoothly. Our
        milestone-based service schedule is designed to guide you through the
        maintenance tasks required at specific mileage markers. By following
        this schedule, you can address potential issues before they become major
        concerns and maintain the value of your vehicle.
      </Text>
      <TabsNavigation
        tabs={maintenanceTabs}
        tab={StyledTab}
        variant="standard"
        sx={{ marginTop: '16px', '& .MuiTabs-indicator': { display: 'none' } }}
      />
    </Card>
  );
}

function generateMajorMaintenanceData(
  schedules: AutomobileMaintenanceSchedules,
  units: string
) {
  const output = schedules.reduce(
    (output: Record<number, ColumnValue[][]>, schedule) => {
      const intervals =
        schedule.intervals?.filter((interval) => {
          return (
            interval.type?.toUpperCase() === 'AT' &&
            interval.units?.toUpperCase() === units.toUpperCase()
          );
        }) || [];

      for (const interval of intervals) {
        const value = interval.value || 0;
        if (value) {
          if (!output[value]) {
            output[value] = [];
          }
          output[value].push([schedule.category || '', schedule.name || '']);
        }
      }

      return output;
    },
    {}
  );

  const sorted = Object.entries(output).sort((a, b) => {
    return parseInt(a[0]) - parseInt(b[0]);
  });

  return sorted.map((maintenance) => {
    return {
      interval: maintenance[0],
      services: maintenance[1],
    };
  });
}
